import React, { FC, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { Close } from '@material-ui/icons'
import { IconButton } from '@material-ui/core'
import kehilaMigrationWizardStore from 'store/kehilaMigrationWizard.store'
import {
  WizardDialog, WizardHeader, WizardTitle, WizardBody, Layout, Rail, Spine, SpineItem,
  Dot, SLabel, SMeta, Stage, StageHeading, Lede, StepBody, Foot, Note, PlainButton, KeyButton,
} from './KehilaMigrationWizard.styles'
import ConnectStep from './steps/ConnectStep'
import MapPermissionsStep from './steps/MapPermissionsStep'
import CreateCategoriesStep from './steps/CreateCategoriesStep'
import ImportProtocolsStep from './steps/ImportProtocolsStep'
import ReportStep from './steps/ReportStep'

// One-time Kehila -> Protocols migration wizard, opened from the admin hamburger menu
// (SuperUserMenu.tsx -> "Migrate Protocols from Kehila"). 
// Temporary feature - will be removed after the migration.

type TFn = (key: string, opts?: any) => string

const STEP_LABEL_KEYS = ['step_connect', 'step_mapPermissions', 'step_createCategories', 'step_importProtocols', 'step_report']

const STEP_COPY_KEYS: [string, string][] = [
  ['copy_connect_h', 'copy_connect_p'],
  ['copy_map_h', 'copy_map_p'],
  ['copy_create_h', 'copy_create_p'],
  ['copy_import_h', 'copy_import_p'],
  ['copy_report_h', 'copy_report_p'],
]

const stepMeta = (store: typeof kehilaMigrationWizardStore, index: number, t: TFn): string => {
  if (index === 0) return store.probed ? store.communityId : t('meta_notTested')
  if (index === 1) return t('meta_kept', { kept: store.liveCategories.length, total: store.categories.length })
  if (index === 2) return store.createResult ? t('meta_created', { num: store.createResult.created }) : t('meta_pending')
  if (index === 3) return store.importStatus ? `${store.importStatus.done} / ${store.importStatus.total}` : t('meta_notStarted')
  return store.importStatus ? t('meta_failed', { num: store.importStatus.failed }) : '-'
}

const STEP_COMPONENTS = [ConnectStep, MapPermissionsStep, CreateCategoriesStep, ImportProtocolsStep, ReportStep]

const KehilaMigrationWizard: FC = () => {
  const store = kehilaMigrationWizardStore
  const { t } = useTranslation('kehilaMigration')

  useEffect(() => () => store.stopPolling(), [])

  const CurrentStep = STEP_COMPONENTS[store.step]
  const blockedReason = store.isNextBlocked

  return (
    <WizardDialog open={store.isOpen} onClose={store.close} maxWidth='md' fullWidth>
      <WizardHeader dir='rtl'>
        <WizardTitle>{t`title`}</WizardTitle>
        <IconButton onClick={store.close}>
          <Close />
        </IconButton>
      </WizardHeader>
      <WizardBody dir='rtl'>
        <Layout>
          <Rail aria-label={t`aria_steps`}>
            <Spine>
              {STEP_LABEL_KEYS.map((labelKey, index) => {
                const status = index === store.step ? 'now' : index < store.step ? 'done' : ''
                return (
                  <SpineItem key={labelKey} status={status}>
                    <Dot status={status}>{index < store.step ? '✓' : index + 1}</Dot>
                    <SLabel status={status}>
                      {t(labelKey)}
                      <SMeta>{stepMeta(store, index, t)}</SMeta>
                    </SLabel>
                  </SpineItem>
                )
              })}
            </Spine>
          </Rail>
          <Stage>
            <StageHeading>{t(STEP_COPY_KEYS[store.step][0])}</StageHeading>
            <Lede>{t(STEP_COPY_KEYS[store.step][1])}</Lede>
            <StepBody>
              <CurrentStep />
            </StepBody>
            <Foot>
              <PlainButton style={{ visibility: store.step === 0 ? 'hidden' : 'visible' }} onClick={store.goBack}>
                {t`btn_back`}
              </PlainButton>
              <span style={{ flex: 1 }} />
              <Note>{blockedReason || ''}</Note>
              <KeyButton disabled={!!blockedReason} onClick={store.goNext}>
                {store.step === 4 ? t`btn_finish` : t`btn_continue`}
              </KeyButton>
            </Foot>
          </Stage>
        </Layout>
      </WizardBody>
    </WizardDialog>
  )
}

export default observer(KehilaMigrationWizard)
